import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '@shared/auth/AuthContext';
import { sessionManager } from './sessionManager';
import { ShieldAlert, RefreshCw, LogOut } from 'lucide-react';
import { logger } from '@shared/logging/logger';

interface SessionContextType {
  lastActive: number;
  isWarningOpen: boolean;
  remainingMs: number;
  keepSessionAlive: () => void;
  logoutSecurely: () => void;
}

const SessionContext = createContext<SessionContextType | undefined>(undefined);

export const SessionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session, logout } = useAuth();
  const [lastActive, setLastActive] = useState<number>(Date.now());
  const [isWarningOpen, setIsWarningOpen] = useState(false);
  const [remainingMs, setRemainingMs] = useState(0);

  const handleKeepAlive = React.useCallback(() => {
    sessionManager.trackActivity();
    setIsWarningOpen(false);
    setRemainingMs(0);
    setLastActive(Date.now());
  }, []);

  const handleLogout = React.useCallback(() => {
    setIsWarningOpen(false);
    sessionManager.handleLogout('manual_timeout');
  }, []);

  useEffect(() => {
    if (!session) {
      setIsWarningOpen(false);
      sessionManager.destroy();
      return;
    }

    // Configure and start session timers
    sessionManager.initialize(
      {
        inactivityTimeoutMs: 20 * 60 * 1000, // 20 mins
        warningTimeoutMs: 15 * 60 * 1000,    // 15 mins warning
      },
      () => {
        logger.warn('security', 'Session absolutely expired. Logging out.');
        logout();
      },
      (timeLeftMs) => {
        setRemainingMs(timeLeftMs);
        setIsWarningOpen(true);
      },
      () => {
        setLastActive(Date.now());
      }
    );

    return () => {
      sessionManager.destroy();
    };
  }, [session, logout]);

  // Track active ticks when warning modal is open
  useEffect(() => {
    let warningTicker: any = null;
    if (isWarningOpen) {
      warningTicker = setInterval(() => {
        setRemainingMs((prev) => {
          if (prev <= 1000) {
            clearInterval(warningTicker);
            return 0;
          }
          return prev - 1000;
        });
      }, 1000);
    }

    return () => {
      if (warningTicker) clearInterval(warningTicker);
    };
  }, [isWarningOpen]);

  // Format countdown
  const secondsLeft = Math.max(0, Math.ceil(remainingMs / 1000));
  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const countdownStr = `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;

  const contextValue = React.useMemo(() => ({
    lastActive,
    isWarningOpen,
    remainingMs,
    keepSessionAlive: handleKeepAlive,
    logoutSecurely: handleLogout
  }), [lastActive, isWarningOpen, remainingMs, handleKeepAlive, handleLogout]);

  return (
    <SessionContext.Provider value={contextValue}>
      {children}

      {/* Enterprise Security Idle Warning Portal */}
      {isWarningOpen && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-sm animate-in fade-in duration-300">
          <div className="max-w-md w-full bg-slate-900 border border-slate-850 p-8 rounded-3xl shadow-2xl text-center space-y-6 animate-in zoom-in-95 duration-300">
            <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center text-amber-500 mx-auto animate-pulse">
              <ShieldAlert className="w-8 h-8" />
            </div>

            <div className="space-y-2">
              <h3 className="text-xl font-black tracking-tight text-white uppercase italic">
                Inatividade Detectada
              </h3>
              <p className="text-sm text-slate-400">
                Sua sessão expirará automaticamente em <span className="font-mono text-amber-400 font-bold">{countdownStr}</span> devido à falta de atividade. Deseja continuar conectado?
              </p>
            </div>

            <div className="flex gap-3 pt-2">
              <button
                id="session-keep-alive-btn"
                onClick={handleKeepAlive}
                className="flex-1 py-3 px-4 bg-emerald-600 hover:bg-emerald-500 text-white font-bold rounded-xl text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2 pointer cursor-pointer"
              >
                <RefreshCw className="w-4 h-4 animate-spin-slow" />
                Continuar Logado
              </button>
              
              <button
                id="session-forced-logout-btn"
                onClick={handleLogout}
                className="flex-1 py-3 px-4 bg-slate-800 hover:bg-slate-700 text-slate-350 font-bold rounded-xl text-xs uppercase tracking-wider transition-all flex items-center justify-center gap-2 pointer cursor-pointer"
              >
                <LogOut className="w-4 h-4" />
                Sair Agora
              </button>
            </div>

            <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest">
              Sessão protegida por criptografia TLS &bull; WasteBank
            </p>
          </div>
        </div>
      )}
    </SessionContext.Provider>
  );
};

export const useSession = () => {
  const context = useContext(SessionContext);
  if (context === undefined) {
    throw new Error('useSession must be used within a SessionProvider');
  }
  return context;
};
